import {
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectorRef,
  Output,
  EventEmitter,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { ToastService } from '../../../ui/services/toast/toast.service';
import { KeycloakProfile } from 'keycloak-js';

@Component({
  standalone: false,
  selector: 'irembogov-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss'],
})
export class ChangePasswordComponent implements OnInit, OnDestroy {
  changePassForm: FormGroup;
  keyUserProfile: KeycloakProfile = {} as KeycloakProfile;
  private subscriptions = new Subscription();
  loading = false;
  errorMessage = '';
  passwordStrength = 0;
  barColors: string[] = ['grey', 'white', 'white', 'white'];
  password = '';
  cpassword = '';
  isLoading = false;
  errorCPassMsg = '';
  showPassword = false;
  cShowPassword = false;

  passwordValidator = Validators.pattern(
    '^(?=.*[0-9])(?=.*[A-Z])(?=.*[a-z])(?=.*[!@#$%^&*.])[a-zA-Z0-9!@#$%^&*.]{8,}'
  );

  @Output() passwordChanged = new EventEmitter<string>();
  @Output() responseReceived = new EventEmitter<any>();

  constructor(
    public activeModal: NgbActiveModal,
    private toastService: ToastService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {
    this.changePassForm = this.fb.group(
      {
        newPassword: new FormControl('', {
          validators: [this.passwordValidator],
          updateOn: 'change',
        }),
        confirmPassword: new FormControl('', {
          validators: [this.passwordValidator],
          updateOn: 'change',
        }),
      },
      { validators: this.passwordsMatchValidator }
    );
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.changePassForm.controls['confirmPassword'].valueChanges.subscribe(
        value => {
          if (this.changePassForm.controls['newPassword'].value !== value) {
            this.errorMessage = 'Passwords do not match';
          } else {
            this.errorMessage = '';
          }
        }
      )
    );

    this.responseReceived.subscribe((response: any) => {
      this.loading = false;
      if (response instanceof Error) {
        this.errorMessage = response.message;
      } else {
        this.toastService.show({
          body: response,
          type: 'success',
          delay: 5000,
        });
        this.activeModal.close('result');
      }
    });
  }

  async onFormSubmit() {
    if (this.changePassForm.invalid) {
      this.errorMessage =
        'Please fill in all required fields and ensure the passwords match and are strong enough';
      return;
    }
    if (this.changePassForm.errors?.['mismatch']) {
      this.errorMessage = 'Passwords do not match';
      return;
    }

    this.errorMessage = '';
    this.loading = true;

    this.passwordChanged.emit(
      this.changePassForm.controls['confirmPassword'].value
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  calculatePasswordStrength(event: Event): void {
    const password = (event.target as HTMLInputElement).value;
    const red = 'rgb(229, 99, 125)';
    const yellow = 'rgb(250, 228,155)';
    const green = 'rgb(139,203,180)';

    this.barColors = [red, 'white', 'white', 'white'];

    if (password.length < 8) {
      this.barColors[0] = 'rgb(229, 99, 125)';
      return;
    }

    if (/[a-z]/.test(password) && /[0-9]/.test(password)) {
      this.barColors[0] = red;
      this.barColors[1] = yellow;
    }

    if (
      /[a-z]/.test(password) &&
      /[A-Z]/.test(password) &&
      /[0-9]/.test(password)
    ) {
      this.barColors[0] = red;
      this.barColors[1] = yellow;
      this.barColors[2] = green;
    }

    if (
      /[a-z]/.test(password) &&
      /[A-Z]/.test(password) &&
      /[0-9]/.test(password) &&
      /[ !.@#$%^&*()_+=[\]{};':"\\|,<>/?-]/.test(password)
    ) {
      this.barColors[0] = red;
      this.barColors[1] = yellow;
      this.barColors[2] = green;
      this.barColors[3] = green;
    }
  }

  private passwordsMatchValidator(form: FormGroup): ValidationErrors {
    const password = form.controls['newPassword'].value;
    const confirmPassword = form.controls['confirmPassword'].value;

    return password === confirmPassword ? {} : { mismatch: true };
  }

  public togglePassword() {
    this.showPassword = !this.showPassword;
  }

  public cTogglePassword() {
    this.cShowPassword = !this.cShowPassword;
  }

  handleError(message: string) {
    this.loading = false;
    this.errorMessage = message;
  }
}
